import * as signalR from '@microsoft/signalr';
import { UserAccount } from '../data/types';
import { getHTTPHeaders, HTTPContentType } from '../data/api/useApi';

export interface SignalRConnectionConfig {
    hubUrl: string;
    automaticReconnect?: boolean;
    reconnectDelays?: number[];
}

export class SignalRServiceManager {
    private static instance: SignalRServiceManager;
    private static primaryAccount: UserAccount;

    private connection: signalR.HubConnection | null = null;
    private eventHandlers: Map<string, Set<(...args: any[]) => void>> = new Map();

    private constructor() { }

    public static async getInstance(primaryAccount: UserAccount): Promise<SignalRServiceManager> {
        // Better comparison - compare meaningful properties instead of reference
        const accountChanged = !this.primaryAccount ||
            primaryAccount.account.accountId !== this.primaryAccount.account.accountId ||
            primaryAccount.user.username !== this.primaryAccount.user.username;

        if (!SignalRServiceManager.instance || accountChanged) {
            this.primaryAccount = primaryAccount;

            if (SignalRServiceManager.instance) {
                await SignalRServiceManager.instance.cleanup(); // Use a dedicated cleanup method
            }

            SignalRServiceManager.instance = new SignalRServiceManager();
            await SignalRServiceManager.instance.start(); // Await the start
        }
        return SignalRServiceManager.instance;
    }

    private maxRetries = 3;
    private retryCount = 0;

    public async start(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            console.warn('SignalR connection already started');
            return;
        }

        const resp = await fetch(`/api/SignalR/negotiate/${SignalRServiceManager.primaryAccount?.account.accountId}/${SignalRServiceManager.primaryAccount?.user.userId}`, {
            method: 'GET',
            headers: getHTTPHeaders(HTTPContentType.json),
        });
        const connectionInfo = await resp.json().catch(() => null);

        if (!!connectionInfo && !!connectionInfo.url) {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(connectionInfo.url, {
                    accessTokenFactory: () => connectionInfo.accessToken
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Add retry delays
                .configureLogging(signalR.LogLevel.Information)
                .build();

            this.setupConnectionEvents();
            this.reattachEventHandlers();

            try {
                await this.connection.start();
                this.retryCount = 0; // Reset on success
            } catch (error) {
                console.error('SignalR Connection Error:', error);
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    await new Promise(resolve => setTimeout(resolve, 2000 * this.retryCount));
                    return this.start();
                }
            }
        }
    }

    public async stop(): Promise<void> {
        if (this.connection) {
            await this.connection.stop();
        }
    }

    public on(eventName: string, callback: (...args: any[]) => void): void {
        if (!this.eventHandlers.has(eventName)) {
            this.eventHandlers.set(eventName, new Set());
        }
        this.eventHandlers.get(eventName)!.add(callback);

        if (this.connection) {
            this.connection.on(eventName, callback);
        }
    }

    public onSingle(eventName: string, callback: (...args: any[]) => void): void {
        if (!this.eventHandlers.has(eventName)) {
            this.eventHandlers.set(eventName, new Set());
        }
        const handlers = this.eventHandlers.get(eventName);
        if (!handlers?.size || handlers.size === 0) {
            handlers!.add(callback);

            if (this.connection) {
                this.connection.on(eventName, callback);
            }
        }
    }

    public off(eventName: string, callback?: (...args: any[]) => void): void {
        if (callback) {
            const handlers = this.eventHandlers.get(eventName);
            if (handlers) {
                handlers.delete(callback);
                if (handlers.size === 0) {
                    this.eventHandlers.delete(eventName);
                }
            }
            if (this.connection) {
                this.connection.off(eventName, callback);
            }
        } else {
            this.eventHandlers.delete(eventName);
            if (this.connection) {
                this.connection.off(eventName);
            }
        }
    }

    public async invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            throw new Error('SignalR connection is not established');
        }
        return await this.connection.invoke<T>(methodName, ...args);
    }

    public async send(methodName: string, ...args: any[]): Promise<void> {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            throw new Error('SignalR connection is not established');
        }
        return await this.connection.send(methodName, ...args);
    }

    public getConnectionState(): signalR.HubConnectionState | null {
        return this.connection?.state ?? null;
    }

    public isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    private setupConnectionEvents(): void {
        if (!this.connection) return;

        this.connection.onreconnecting((error) => {
            console.warn('SignalR Reconnecting...', error);
        });

        this.connection.onreconnected((connectionId) => {
            console.log('SignalR Reconnected:', connectionId);
        });

        this.connection.onclose((error) => {
            console.error('SignalR Connection Closed:', error);
        });
    }

    private reattachEventHandlers(): void {
        if (!this.connection) return;

        this.eventHandlers.forEach((callbacks, eventName) => {
            callbacks.forEach((callback) => {
                this.connection!.on(eventName, callback);
            });
        });
    }

    private detachEventHandlers(): void {
        if (!this.connection) return;

        this.eventHandlers.forEach((callbacks, eventName) => {
            callbacks.forEach((callback) => {
                this.connection!.off(eventName, callback);
            });
        });
    }

    // Add a dedicated cleanup method
    public async cleanup(): Promise<void> {
        this.detachEventHandlers();
        this.eventHandlers.clear(); // Clear the Map
        await this.stop();
        this.connection = null; // Explicitly nullify
    }

}

export default SignalRServiceManager;